import React, { useState, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sun, Zap, Home } from "lucide-react";
import { AuthContext } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await login(email, password);
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Veuillez saisir votre adresse email");
      return;
    }

    setResetLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success("Email de réinitialisation envoyé ! Vérifiez votre boîte mail.");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      console.error("Password reset error:", error);
      if (error.response?.status === 404) {
        toast.error("Aucun compte trouvé avec cette adresse email");
      } else {
        toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-slate-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-red-50/20 to-orange-50/20 opacity-30"></div>
      
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
        <div className="hidden lg:block space-y-8">
          <div className="flex flex-col items-center lg:items-start space-y-2">
            <img 
              src="/logo-sunlife.jpg"
              alt="SUNLIFE Logo"
              className="h-24 w-auto object-contain"
            />
            <p className="text-slate-600 font-medium text-sm tracking-wide">CRM DE GESTION</p>
          </div>

          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 leading-tight">
              L'énergie solaire à portée de main
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Gérez vos projets d'installation photovoltaïque, pompes à chaleur et dossiers d'aides CEE/MaPrimeRénov en toute simplicité.
            </p>
          </div>

          <div className="grid gap-4">
            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-red-100 rounded-lg">
                <Sun className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Installations Photovoltaïques</h3>
                <p className="text-slate-600 text-sm">Gestion complète de vos projets solaires</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Dossiers d'Aides</h3>
                <p className="text-slate-600 text-sm">CEE et MaPrimeRénov automatisés</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-white/50">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Rénovation Énergétique</h3>
                <p className="text-slate-600 text-sm">Pompes à chaleur, isolation, VMC</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md mx-auto">
          {/* Logo mobile */}
          <div className="lg:hidden flex flex-col items-center mb-6">
            <img src="/logo-sunlife.jpg" alt="SUNLIFE" className="h-16 w-auto object-contain" />
            <p className="text-slate-500 text-xs mt-1">CRM DE GESTION</p>
          </div>
          {!showForgotPassword ? (
            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
              <CardHeader className="space-y-1 pb-6 pt-6">
                <CardTitle className="text-xl md:text-2xl font-bold text-center">Connexion</CardTitle>
                <CardDescription className="text-center text-slate-600 text-sm">
                  Accédez à votre espace SUNLIFE
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                      data-testid="email-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12"
                      data-testid="password-input"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
                    disabled={isLoading}
                    data-testid="login-button"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Connexion...</span>
                      </div>
                    ) : (
                      "Se connecter"
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <Button
                    variant="link"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Mot de passe oublié ?
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
              <CardHeader className="space-y-1 pb-8">
                <CardTitle className="text-2xl font-bold text-center">Mot de passe oublié</CardTitle>
                <CardDescription className="text-center text-slate-600">
                  Entrez votre email pour réinitialiser votre mot de passe
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Envoi..." : "Envoyer le lien"}
                  </Button>
                </form>

                <div className="text-center">
                  <Button
                    variant="link"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Retour à la connexion
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-slate-500 text-sm mt-8">
            © 2025 SUNLIFE. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;